import type { ReactElement } from 'react';
import { paisa } from '@fleet/shared';
import { useExtrasQuery, useRateCardsQuery, useSeasonsQuery } from '@/api/endpoints';
import type { Extra, RateCard, SeasonRule } from '@/api/types';
import { useCan } from '@/app/session';
import { LinkButton } from '@/design/Button';
import { MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import {
  CATEGORY_LABELS,
  EXTRA_UNIT_LABELS,
  RATE_TYPE_LABELS,
  TRIP_AREA_LABELS,
} from '../fleet/labels';

/**
 * Rates.
 *
 * Three tables, because they answer three different questions: what a day
 * costs, when that changes, and what else gets added. Everything is quoted from
 * these — a wrong number here is wrong on every booking until someone notices,
 * which is why editing needs a manager.
 */
export function RatesPage(): ReactElement {
  const cards = useRateCardsQuery();
  const seasons = useSeasonsQuery();
  const extras = useExtrasQuery();
  const canEdit = useCan('rates.edit');

  const cardColumns: readonly Column<RateCard>[] = [
    {
      key: 'applies',
      header: 'Applies to',
      render: (card) =>
        card.vehicle_name !== null && card.vehicle_name !== undefined ? (
          <span>{card.vehicle_name}</span>
        ) : (
          CATEGORY_LABELS[card.category]
        ),
    },
    {
      key: 'driver',
      header: 'Driver',
      render: (card) => (card.with_driver === false ? 'Self-drive' : 'With driver'),
    },
    {
      // Beside the driver column, because the two together are what identify a
      // card: the same category has four of them.
      key: 'area',
      header: 'Where',
      render: (card) => TRIP_AREA_LABELS[card.area ?? 'inside_valley'],
    },
    {
      key: 'rate_type',
      header: 'Charged',
      secondary: true,
      render: (card) => RATE_TYPE_LABELS[card.rate_type ?? 'per_day'],
    },
    {
      key: 'base',
      header: 'Rate',
      numeric: true,
      render: (card) => <MoneyText amount={paisa(card.base_amount)} />,
    },
    {
      key: 'included_km',
      header: 'Included',
      numeric: true,
      secondary: true,
      render: (card) =>
        (card.included_km ?? 0) === 0 ? (
          <span className="text-ink-muted">Unlimited</span>
        ) : (
          <span className="tnum">{card.included_km} km</span>
        ),
    },
    {
      key: 'per_km',
      header: 'Extra km',
      numeric: true,
      secondary: true,
      render: (card) =>
        (card.included_km ?? 0) === 0 ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <MoneyText amount={paisa(card.per_km_amount ?? 0)} />
        ),
    },
    {
      key: 'min_days',
      header: 'Minimum',
      numeric: true,
      secondary: true,
      render: (card) => <span className="tnum">{card.min_days ?? 1}</span>,
    },
    {
      key: 'active',
      header: 'In use',
      render: (card) => (
        <StatusPill
          status={
            card.is_active === false
              ? { label: 'Retired', tone: 'neutral', shape: 'cross' }
              : { label: 'In use', tone: 'active', shape: 'square' }
          }
        />
      ),
    },
  ];

  const seasonColumns: readonly Column<SeasonRule>[] = [
    { key: 'name', header: 'Season', render: (season) => season.name },
    {
      key: 'dates',
      header: 'Dates',
      render: (season) => (
        <span className="tnum">
          {season.start_date} to {season.end_date}
        </span>
      ),
    },
    {
      key: 'multiplier',
      header: 'Rate becomes',
      numeric: true,
      render: (season) => <span className="tnum">{season.multiplier ?? '1.00'}×</span>,
    },
  ];

  const extraColumns: readonly Column<Extra>[] = [
    { key: 'name', header: 'Extra', render: (extra) => extra.name },
    {
      key: 'price',
      header: 'Price',
      numeric: true,
      render: (extra) => <MoneyText amount={paisa(extra.price)} />,
    },
    {
      key: 'unit',
      header: 'Charged',
      render: (extra) => EXTRA_UNIT_LABELS[extra.unit ?? 'per_booking'],
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Rates</h1>
        {canEdit && (
          <LinkButton to="/rates/new" intent="primary" size="touch">
            <Icon name="plus" width="16" height="16" />
            Add rate card
          </LinkButton>
        )}
      </div>

      <Panel
        title="Rate cards"
        note="A card naming one vehicle beats a card naming its whole category"
        current
      >
        <DataTable
          columns={cardColumns}
          rows={cards.data?.results ?? []}
          rowKey={(card) => card.id}
          rowHref={canEdit ? (card) => `/rates/${card.id}` : undefined}
          isLoading={cards.isLoading}
          isError={cards.isError}
          onRetry={() => void cards.refetch()}
          errorHeadline="The rate cards did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: 'No rate cards yet',
            detail:
              'A rate card sets the daily amount for a category, with or without a driver, the kilometres included and the charge beyond them.',
            action: canEdit ? (
              <LinkButton to="/rates/new" intent="primary">
                <Icon name="plus" width="16" height="16" />
                Add rate card
              </LinkButton>
            ) : undefined,
          }}
        />
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Seasons" note="When the base rate moves, and by how much">
          <DataTable
            columns={seasonColumns}
            rows={seasons.data?.results ?? []}
            rowKey={(season) => season.id}
            isLoading={seasons.isLoading}
            isError={seasons.isError}
            onRetry={() => void seasons.refetch()}
            errorHeadline="The seasons did not load"
            errorDetail="The server did not answer. Load them again."
            empty={{
              headline: 'No seasons set',
              detail:
                'Add a season to raise or lower every rate over a date range — Dashain, or the trekking weeks in October.',
            }}
          />
        </Panel>

        <Panel title="Extras" note="Added to a booking as needed">
          <DataTable
            columns={extraColumns}
            rows={extras.data?.results ?? []}
            rowKey={(extra) => extra.id}
            isLoading={extras.isLoading}
            isError={extras.isError}
            onRetry={() => void extras.refetch()}
            errorHeadline="The extras did not load"
            errorDetail="The server did not answer. Load them again."
            empty={{
              headline: 'No extras yet',
              detail:
                'A child seat, an airport pickup, a second driver. Each one has a price and how it is charged.',
            }}
          />
        </Panel>
      </div>
    </div>
  );
}
